import React from "react";
import { makeStyles, Paper, TextField, Divider } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  rowContainer: {
    marginBottom: "3rem",
    overflow: "hidden",
    border: "1px solid #e0e0e0",
    borderRadius: "8px"
  },
  headerBar: {
    background: "#f0f0f0",
    padding: "0.8rem 1.5rem",
    borderBottom: "1px solid #e0e0e0",
    color: "#555",
    fontSize: "1rem",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  },
  // --- GRID LAYOUT ---
  headerRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
  },
  columnHeader: {
    padding: "0.8rem 1.5rem",
    fontSize: "0.85rem",
    fontWeight: "bold",
    textTransform: "uppercase",
    color: "white",
    letterSpacing: '1px',
    textAlign: "left"
  },
  headerLeft: {
    background: "#666",
    borderRight: "1px solid #777"
  },
  headerRight: {
    background: "var(--garnish-primary-green)",
  },
  
  // --- ALIGNMENT ROWS ---
  alignmentRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    borderBottom: "1px solid #eee",
    "&:last-child": {
      borderBottom: "none"
    }
  },
  
  // --- CELLS ---
  cellLeft: {
    padding: "1.5rem",
    borderRight: "1px solid #eee",
    background: "#fdfdfd", 
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    userSelect: "none" // Prevents text selection to further discourage interaction
  },
  cellRight: {
    padding: "1.5rem",
    background: "white",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
  },

  // --- CONTENT STYLES ---
  label: {
    fontSize: "0.85rem",
    fontWeight: "bold",
    color: "#888",
    marginBottom: "0.6rem",
    textTransform: "uppercase",
    textAlign: "left", 
    display: "block"
  },
  // --- UPDATED: NON-INTERACTIVE STYLE ---
  readOnlyInput: {
    pointerEvents: "none", // Disables clicking, hovering, focusing
    "& .MuiOutlinedInput-root": {
      background: "#f5f5f5", 
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderWidth: "1px",
        borderColor: "#e0e0e0" // Force gray border even if focused programmatically
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#e0e0e0" // Force gray border on hover
      }
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e0e0e0" 
    },
    "& .MuiInputBase-input": {
      color: "#555", 
      fontWeight: 500
    }
  },
  inputField: {
    background: "white"
  },
  feedbackInput: {
    background: "#f9fcfb"
  },
  sectionDivider: {
    height: "10px",
    background: "#f8f8f8",
    borderTop: "1px solid #e0e0e0",
    borderBottom: "1px solid #e0e0e0"
  }
}));

export default function QuestionTranslationRow({ 
  question, 
  index,
  languageId, 
  targetLanguageName,
  onUpdate 
}) {
  const classes = useStyles();

  const getTranslatedText = (obj, field) => {
    const translations = obj[`${field}Translations`] || [];
    const entry = translations.find((t) => t.languageId === languageId);
    return entry ? entry.translatedValue : "";
  };

  const updateTranslatedText = (obj, field, value) => {
    const translations = [...(obj[`${field}Translations`] || [])];
    const index = translations.findIndex((t) => t.languageId === languageId);

    if (index > -1) {
      translations[index] = { ...translations[index], translatedValue: value };
    } else {
      translations.push({ languageId: languageId, translatedValue: value });
    }
    return { ...obj, [`${field}Translations`]: translations };
  };

  const handleQuestionChange = (val) => {
    const updatedQ = updateTranslatedText(question, "questionText", val);
    onUpdate(updatedQ);
  };

  const handleAnswerFieldChange = (idx, field, val) => {
    let updatedQ = { ...question };
    const newAnswers = [...updatedQ.answers];
    newAnswers[idx] = updateTranslatedText(newAnswers[idx], field, val);
    updatedQ.answers = newAnswers;
    onUpdate(updatedQ);
  };

  return (
    <Paper className={classes.rowContainer} elevation={2}>
      <div className={classes.headerBar}>
        Question {index + 1}
      </div>

      <div className={classes.headerRow}>
        <div className={`${classes.columnHeader} ${classes.headerLeft}`}>
          Original (English)
        </div>
        <div className={`${classes.columnHeader} ${classes.headerRight}`}>
          {targetLanguageName}
        </div>
      </div>

      {/* --- QUESTION TEXT ROW --- */}
      <div className={classes.alignmentRow}>
        <div className={classes.cellLeft}>
          <div className={classes.label}>Question Text</div>
          <TextField
            fullWidth
            variant="outlined"
            multiline
            minRows={2}
            value={question.questionText || ""}
            className={classes.readOnlyInput}
            InputProps={{
              readOnly: true,
            }}
          />
        </div>
        <div className={classes.cellRight}>
          <div className={classes.label} style={{ color: 'var(--garnish-primary-green)'}}>
            Translated Question
          </div>
          <TextField
            fullWidth
            variant="outlined"
            multiline
            minRows={2}
            className={classes.inputField}
            value={getTranslatedText(question, "questionText")}
            onChange={(e) => handleQuestionChange(e.target.value)}
            placeholder="Translate question..."
          />
        </div>
      </div>

      {/* --- ANSWERS LOOP --- */}
      {question.answers.map((a, i) => (
        <React.Fragment key={a.id || i}>
          
          <div className={classes.sectionDivider}></div>

          {/* ANSWER TEXT ROW */}
          <div className={classes.alignmentRow}>
            <div className={classes.cellLeft}>
               <div className={classes.label}>Option {i + 1}</div>
               <TextField
                  fullWidth
                  variant="outlined"
                  value={a.answerText || ""}
                  className={classes.readOnlyInput}
                  InputProps={{
                    readOnly: true,
                  }}
               />
            </div>
            <div className={classes.cellRight}>
               <div className={classes.label} style={{ color: 'var(--garnish-primary-green)'}}>
                   Translated Option {i + 1}
               </div>
               <TextField
                  fullWidth
                  variant="outlined"
                  className={classes.inputField}
                  value={getTranslatedText(a, "answerText")}
                  onChange={(e) => handleAnswerFieldChange(i, "answerText", e.target.value)}
                  placeholder={`Translate option ${i + 1}...`}
               />
            </div>
          </div>

          {/* FEEDBACK TEXT ROW (Only if exists in English) */}
          {a.feedbackText && (
            <div className={classes.alignmentRow}>
               <div className={classes.cellLeft}>
                 <div className={classes.label}>Option {i + 1} Feedback</div>
                 <TextField
                    fullWidth
                    variant="outlined"
                    multiline
                    value={a.feedbackText || ""}
                    className={classes.readOnlyInput}
                    InputProps={{
                      readOnly: true,
                    }}
                 />
               </div>
               <div className={classes.cellRight}>
                 <div className={classes.label} style={{ color: 'var(--garnish-primary-green)'}}>
                     Translated Feedback
                 </div>
                 <TextField
                   fullWidth
                   variant="outlined"
                   multiline
                   className={`${classes.inputField} ${classes.feedbackInput}`}
                   value={getTranslatedText(a, "feedbackText")}
                   onChange={(e) => handleAnswerFieldChange(i, "feedbackText", e.target.value)}
                   placeholder={`Translate feedback...`}
                 />
               </div>
            </div>
          )}
        </React.Fragment>
      ))}
    </Paper>
  );
}